import React from 'react'
import { Box, Tooltip } from '@mui/material'
import { styled } from '@mui/material/styles'
import { StudioTopTab } from '../../types'
import { monoLabelSx, STUDIO_ACCENT } from '../shared'
import { useTopicViewers } from '@/hooks/common/useTopicViewers'
import { getPresenceAvatarTone, isValidAvatarUrl } from '@/utils/helper'
import { LogoMark } from '@/features/album/components/EgowallLogo'

export interface StudioTopBarProps {
  ownerName: string
  fileName: string
  /** Gallery topicID — drives the real "viewed recently" presence indicator (see useTopicViewers). Omit to just show the owner's own avatar. */
  topicID?: string
  tab: StudioTopTab
  onTabChange: (tab: StudioTopTab) => void
  saving: boolean
  savedVersion: number
  onShare: () => void
  onPresent: () => void
  /** Capture the live scene and save the frame to an album. */
  onScreenshot?: () => void
  screenshotBusy?: boolean
  /** Clicking the logo mark exits the editor back to the gallery library — same "logo = home" convention as AlbumShellLayout's EgowallLogo. */
  onLogoClick: () => void
  /** Hides Present entirely. Not currently used for Classic rooms — those still present fine, just orbit-locked instead of walkable (see useStudioCameraRig's enterPresent) — kept as a general escape hatch for future callers. */
  presentDisabled?: boolean
  /** Dev-only — opens Babylon's debugLayer inspector on the live scene. Omit this prop entirely in production (see Studio3DScreen, gated on NODE_ENV). */
  onToggleInspector?: () => void
}

/** Exported so other overlays (e.g. the dev Babylon Inspector's own docking offset) can stay in sync without duplicating the number. */
export const STUDIO_TOPBAR_HEIGHT = 46

/** Caps for the presence indicator — a viral/public gallery can rack up many recent viewers; keep the avatar stack and tooltip readable instead of growing unbounded. */
const MAX_VISIBLE_VIEWER_AVATARS = 3
const MAX_TOOLTIP_NAMES = 5

const Root = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: STUDIO_TOPBAR_HEIGHT,
  zIndex: 40,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '0 12px',
  background: 'rgba(14, 13, 18, 0.78)',
  borderBottom: '1px solid var(--ds-night-border)',
  backdropFilter: 'blur(18px) saturate(1.3)',
  WebkitBackdropFilter: 'blur(18px) saturate(1.3)',
  color: 'var(--ds-night-ink)',
})

const Crumb = styled(Box)({ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, minWidth: 0 })
const Owner = styled('span')({ color: 'var(--ds-night-ink-subtle)', fontWeight: 500 })
const Sep = styled('span')({ color: 'var(--ds-night-ink-subtle)', opacity: 0.5 })
const FileName = styled('span')({ fontWeight: 680, letterSpacing: '-0.01em', whiteSpace: 'nowrap' })

const RoomChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  height: 21,
  padding: '0 8px',
  borderRadius: 6,
  background: 'rgba(244,243,246,0.06)',
  border: '1px solid var(--ds-night-border)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  color: 'var(--ds-night-ink-muted)',
  cursor: 'pointer',
  flexShrink: 0,
  '&:hover': { background: 'rgba(244,243,246,0.12)' },
})

/**
 * Same look as RoomChip, minus the button affordance. Real room switching
 * (multiple rooms per gallery) isn't built yet — deferred, not soft-gated —
 * so this reads as a plain location label instead of a dead/misleading control.
 */
const RoomLabel = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  height: 21,
  padding: '0 8px',
  borderRadius: 6,
  background: 'rgba(244,243,246,0.06)',
  border: '1px solid var(--ds-night-border)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  color: 'var(--ds-night-ink-muted)',
  flexShrink: 0,
})

const SaveChip = styled(Box, { shouldForwardProp: (p) => p !== '$saving' })<{ $saving?: boolean }>(({ $saving }) => ({
  ...monoLabelSx,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  '&::before': {
    content: '""',
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: $saving ? STUDIO_ACCENT.gold : '#58d68d',
    transition: 'background 140ms ease',
  },
}))

const Tabs = styled(Box)({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: 2,
  background: 'rgba(244,243,246,0.05)',
  border: '1px solid var(--ds-night-border)',
  borderRadius: 9,
  padding: 2,
})

const TabBtn = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  height: 26,
  padding: '0 14px',
  borderRadius: 7,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  color: $on ? 'var(--ds-night-ink)' : 'var(--ds-night-ink-subtle)',
  background: $on ? 'rgba(244,243,246,0.1)' : 'transparent',
  transition: 'all 140ms ease',
}))

const Right = styled(Box)({ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 })

const ShareBtn = styled('button')({
  all: 'unset',
  height: 28,
  padding: '0 13px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  background: 'rgba(244,243,246,0.08)',
  border: '1px solid var(--ds-night-border)',
  color: 'var(--ds-night-ink)',
  '&:hover': { background: 'rgba(244,243,246,0.15)' },
})

const ShotBtn = styled('button', { shouldForwardProp: (p) => p !== '$busy' })<{ $busy?: boolean }>(({ $busy }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  height: 28,
  padding: '0 12px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 650,
  cursor: $busy ? 'default' : 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  color: $busy ? STUDIO_ACCENT.gold : 'var(--ds-night-ink)',
  background: $busy ? 'rgba(255,207,125,0.14)' : 'rgba(244,243,246,0.08)',
  border: `1px solid ${$busy ? 'rgba(255,207,125,0.45)' : 'var(--ds-night-border)'}`,
  opacity: $busy ? 0.9 : 1,
  transition: 'all 140ms ease',
  '&:hover': $busy
    ? undefined
    : {
        background: 'rgba(255,207,125,0.14)',
        borderColor: 'rgba(255,207,125,0.4)',
        color: STUDIO_ACCENT.gold,
      },
  '&:disabled': { cursor: 'default' },
}))

const ShotGlyph = styled('span')({
  width: 14,
  height: 14,
  borderRadius: 4,
  border: '1.5px solid currentColor',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  flexShrink: 0,
  '&::after': {
    content: '""',
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: 'currentColor',
  },
})

// 1:1 with the mockup's `.presence .pa` — gradient "you" avatar with a border
// matching the dark canvas bg (a subtle cutout ring, not a bright halo).
// Deliberately NOT the design system's `AvatarStack` — that one borders each
// avatar with `--ds-canvas` (#fff, a light-theme token), which paints a
// glaring white ring on this dark top bar.
const PresenceAvatar = styled(Box)({
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 9.5,
  fontWeight: 800,
  color: '#fff',
  background: 'var(--ds-gradient-spectrum)',
  border: '2px solid var(--ds-night-canvas)',
  cursor: 'default',
})

/** A real recent viewer, stacked behind the owner's own avatar (see PresenceAvatar). */
const ViewerAvatar = styled(PresenceAvatar)({
  marginLeft: -8,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
})

/** "+N" overflow chip when more people viewed recently than fit in the stack — same shape as ViewerAvatar, neutral (non-tone) fill so it doesn't read as a real person. Needs to be a real opaque color, not a faint wash — it sits directly on the dark toolbar backdrop and a low-alpha tint just disappears into it. */
const ViewerOverflow = styled(ViewerAvatar)({
  background: 'rgba(244, 243, 246, 0.6)',
  color: 'var(--ds-night-canvas)',
  fontSize: 8.5,
})

const PresentBtn = styled('button')({
  all: 'unset',
  position: 'relative',
  height: 28,
  padding: '0 14px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 650,
  cursor: 'pointer',
  background: '#0d0d0f',
  color: '#fff',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -2,
    borderRadius: 999,
    background: 'var(--ds-gradient-spectrum)',
    zIndex: -1,
  },
})

/** Fixed top chrome — crumb, save status, Design/Tour tabs, presence, share, present. */
export function StudioTopBar({
  ownerName,
  fileName,
  topicID,
  tab,
  onTabChange,
  saving,
  savedVersion,
  onShare,
  onPresent,
  onScreenshot,
  screenshotBusy,
  onLogoClick,
  presentDisabled,
  onToggleInspector,
}: StudioTopBarProps) {
  const recentViewers = useTopicViewers(topicID)
  const visibleViewers = recentViewers.slice(0, MAX_VISIBLE_VIEWER_AVATARS)
  const overflowCount = recentViewers.length - visibleViewers.length

  const presenceTitle = (() => {
    if (!recentViewers.length) return 'You'
    const names = recentViewers.map((v) => v.sourceUserFullName)
    if (names.length <= MAX_TOOLTIP_NAMES) return `Viewed recently by ${names.join(', ')}`
    const shownNames = names.slice(0, MAX_TOOLTIP_NAMES)
    return `Viewed recently by ${shownNames.join(', ')} and ${names.length - shownNames.length} more`
  })()

  return (
    <Root>
      <Tooltip title='Back to your galleries' arrow>
        <LogoMark size={20} onClick={onLogoClick} sx={{ cursor: 'pointer' }} />
      </Tooltip>
      <Crumb>
        <Owner>{ownerName}</Owner>
        <Sep>/</Sep>
        <FileName>{fileName}</FileName>
        <RoomLabel>◈ MAIN ROOM</RoomLabel>
        {/*
          ◇ HALL / ◈ SPACES chips used to live here, jumping to the
          `present-realistic` / `virtual-spaces` prototype routes. Both
          screens still exist, still run, and are still reachable by typing
          the URL directly — they're just no longer linked from the live
          editor. They're unsaved, un-billed Babylon prototypes toward a
          future walkable multi-room gallery, not a shipped feature, and a
          real user stumbling into one from here read as a broken promise
          rather than a preview. Re-add a link here behind a real feature
          flag if/when one of these graduates into product work.
        */}
      </Crumb>
      <SaveChip $saving={saving}>{saving ? 'SAVING…' : `SAVED · V${savedVersion}`}</SaveChip>

      <Tabs>
        <TabBtn $on={tab === 'design'} onClick={() => onTabChange('design')}>
          Design
        </TabBtn>
        <TabBtn $on={tab === 'tour'} onClick={() => onTabChange('tour')}>
          Tour
        </TabBtn>
      </Tabs>

      <Right>
        {onToggleInspector && (
          <Tooltip title='Dev only — open Babylon Inspector' arrow>
            <RoomChip onClick={onToggleInspector}>🐞 INSPECT</RoomChip>
          </Tooltip>
        )}
        <Tooltip title={presenceTitle} arrow>
          <Box sx={{ display: 'flex' }}>
            <PresenceAvatar>{ownerName[0]?.toUpperCase() || 'Y'}</PresenceAvatar>
            {visibleViewers.map((viewer) => {
              const hasPhoto = isValidAvatarUrl(viewer.profilePicThumbURL || '')
              const tone = getPresenceAvatarTone(viewer.sourceUserID)
              return (
                <ViewerAvatar
                  key={viewer.sourceUserID}
                  style={
                    hasPhoto
                      ? { backgroundImage: `url(${viewer.profilePicThumbURL})` }
                      : { background: tone.bg, color: tone.ink }
                  }
                >
                  {!hasPhoto && viewer.sourceUserFullName?.[0]?.toUpperCase()}
                </ViewerAvatar>
              )
            })}
            {overflowCount > 0 && <ViewerOverflow>+{overflowCount}</ViewerOverflow>}
          </Box>
        </Tooltip>
        {onScreenshot && (
          <Tooltip title='Capture this view and save it to an album' arrow>
            <span>
              <ShotBtn type='button' onClick={onScreenshot} disabled={screenshotBusy} $busy={screenshotBusy} aria-label='Take screenshot'>
                <ShotGlyph aria-hidden />
                {screenshotBusy ? 'Capturing…' : 'Screenshot'}
              </ShotBtn>
            </span>
          </Tooltip>
        )}
        <ShareBtn onClick={onShare}>Share</ShareBtn>
        {!presentDisabled && <PresentBtn onClick={onPresent}>▶ Present</PresentBtn>}
      </Right>
    </Root>
  )
}
